<script setup lang="ts">
import Button from '../design-system/button.vue';
import Drawer from '~/components/design-system/drawer.vue';

const runtimeConfig = useRuntimeConfig();
const siteStore = useSiteStore();

const active = ref(false);
const searchText = ref('');

function formatValue(value: unknown) {
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  return String(value);
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

const rows = computed(() => {
  const query = searchText.value.toLowerCase();
  return Object.entries(runtimeConfig.public)
    .map(([key, value]) => ({ key, value: formatValue(value) }))
    .filter(({ key, value }) => `${key} ${value}`.toLowerCase().includes(query))
    .sort((a, b) => a.key.localeCompare(b.key));
});
</script>
<template>
  <div>
    <Drawer
      v-model:visible="active"
      position="bottom"
      custom-container-class="bg-base-secondary p-4 overflow-y-auto border-none"
    >
      <template #activator>
        <Button
          class="font-normal w-full justify-center"
          type="primary"
          padding="md"
          rounding="md"
          @click="active = true"
        >
          View environment variables
        </Button>
        <div class="text-base-priority text-xs mt-1">
          Current environment: {{ siteStore.getEnvironment }}
        </div>
      </template>
      <template #header>
        <div class="flex-center flex-wrap gap-3 mb-2">
          <p class="text-base-priority text-lg font-bold mb-1">
            Environment Variables
          </p>
          <label for="env-var-search" class="text-base-priority ml-3">
            Search
          </label>
          <input
            id="env-var-search"
            v-model="searchText"
            name="search"
            class="pl-3 p-2 text-md text-base-priority bg-layer h-min rounded-md stacked-border"
          />
        </div>
      </template>
      <div class="text-base-priority max-h-[60vh] overflow-auto">
        <table class="w-full text-left text-sm">
          <thead class="sticky top-0 bg-base-secondary">
            <tr>
              <th class="p-2 font-bold">Key</th>
              <th class="p-2 font-bold">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.key"
              class="border-t stacked-border-color"
            >
              <td class="p-2 align-top font-mono">{{ row.key }}</td>
              <td
                class="p-2 align-top font-mono break-all cursor-copy"
                :title="row.value"
                @click="copyToClipboard(row.value)"
              >
                {{ row.value }}
              </td>
            </tr>
            <tr v-if="rows.length === 0">
              <td class="p-2" colspan="2">No matching variables</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Drawer>
  </div>
</template>
